import { ArrowLeft, Scale, FileText, AlertTriangle, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const LegalDisclaimers = () => {
  const disclaimers = [
    {
      id: 1,
      title: 'System Guidance Disclaimer',
      category: 'General',
      description: 'Main system disclaimer regarding guidance vs. compliance responsibility',
      content: 'Crystal HACCP provides guidance only. Operators remain fully responsible for compliance with all applicable food safety regulations.',
      lastUpdated: '2024-01-15',
      regions: ['All Regions'],
      status: 'Active'
    },
    {
      id: 2,
      title: 'EU Food Safety Compliance Notice',
      category: 'Regional',
      description: 'Specific disclaimer for European Union food safety regulations',
      content: 'This system provides guidance based on EU Regulation (EC) No 852/2004 and related legislation. Users must ensure compliance with national implementations.',
      lastUpdated: '2024-01-10',
      regions: ['EU/NO'],
      status: 'Active'
    },
    {
      id: 3,
      title: 'Temperature Monitoring Disclaimer',
      category: 'Technical',
      description: 'Disclaimer regarding automated temperature monitoring systems',
      content: 'Automated temperature alerts are provided as guidance. Manual verification and appropriate corrective actions remain the operator\'s responsibility.',
      lastUpdated: '2024-01-08',
      regions: ['All Regions'],
      status: 'Active'
    },
    {
      id: 4,
      title: 'Data Processing Notice',
      category: 'Privacy',
      description: 'Legal notice regarding food safety data processing and storage',
      content: 'Food safety data is processed for compliance purposes in accordance with applicable data protection regulations including GDPR.',
      lastUpdated: '2024-01-05',
      regions: ['EU/NO', 'UK'],
      status: 'Active'
    }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'General': return 'bg-blue-500/20 text-blue-300';
      case 'Regional': return 'bg-green-500/20 text-green-300';
      case 'Technical': return 'bg-purple-500/20 text-purple-300';
      case 'Privacy': return 'bg-yellow-500/20 text-yellow-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-section mb-2">Legal Disclaimers</h1>
              <p className="text-muted-foreground">Manage legal disclaimers and compliance notices</p>
            </div>
            <Button className="cta-primary">
              <FileText className="h-4 w-4 mr-2" />
              Add Disclaimer
            </Button>
          </div>
        </div>

        {/* Primary Disclaimer Notice */}
        <Card className="glass-panel border-0 mb-8 border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-orange-500 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-3">Important Legal Notice</h3>
                <div className="prose prose-sm text-muted-foreground mb-4">
                  <p className="mb-3">
                    <strong>Crystal HACCP provides guidance only.</strong> This system is designed to assist food service operators 
                    in maintaining food safety standards, but operators remain fully responsible for compliance with all applicable 
                    food safety regulations and laws.
                  </p>
                  <p className="mb-3">
                    The system's recommendations, checklists, and monitoring features are intended as tools to support compliance 
                    efforts but do not replace the need for proper training, professional judgment, and adherence to local, 
                    national, and international food safety requirements.
                  </p>
                  <p>
                    Users must ensure that all activities comply with relevant regulations in their jurisdiction, including but 
                    not limited to HACCP principles, food safety management systems, and applicable health codes.
                  </p>
                </div>
                <Button variant="outline" size="sm" className="glass-button">
                  <Download className="h-4 w-4 mr-2" />
                  Download Full Disclaimer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimers List */}
        <div className="space-y-4">
          {disclaimers.map((disclaimer) => (
            <Card key={disclaimer.id} className="glass-panel border-0">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-2 glass-button rounded-lg">
                      <Scale className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{disclaimer.title}</h3>
                        <Badge className={getCategoryColor(disclaimer.category)}>
                          {disclaimer.category}
                        </Badge>
                        <Badge className="bg-green-500/20 text-green-300">
                          {disclaimer.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{disclaimer.description}</p>
                      <div className="bg-muted/20 p-3 rounded-lg mb-3">
                        <p className="text-sm italic">{disclaimer.content}</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Regions: {disclaimer.regions.join(', ')}</span>
                        <span>Last Updated: {disclaimer.lastUpdated}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="glass-button">
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" className="glass-button">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Disclaimer Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {disclaimers.filter(d => d.category === 'General').length}
                </div>
                <div className="text-sm text-muted-foreground">General Disclaimers</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {disclaimers.filter(d => d.category === 'Regional').length}
                </div>
                <div className="text-sm text-muted-foreground">Regional Notices</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {disclaimers.filter(d => d.category === 'Technical').length}
                </div>
                <div className="text-sm text-muted-foreground">Technical Disclaimers</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">
                  {disclaimers.filter(d => d.category === 'Privacy').length}
                </div>
                <div className="text-sm text-muted-foreground">Privacy Notices</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Legal Requirements Notice */}
        <Card className="glass-panel border-0 mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Legal Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>
                <strong>Regulatory Compliance:</strong> All disclaimers must comply with local legal requirements 
                and industry standards in the jurisdictions where the system operates.
              </p>
              <p>
                <strong>Regular Review:</strong> Legal disclaimers should be reviewed quarterly and updated 
                as regulations change or new features are introduced.
              </p>
              <p>
                <strong>User Acknowledgment:</strong> Critical disclaimers may require explicit user acknowledgment 
                before system access is granted.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LegalDisclaimers;